import React, { useState, useEffect } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { autoPopulateFacilities, checkFacilityLimit } from '../../../services/facilityAutoPopulation'
import { supabase } from '../../../lib/supabase'
import toast from 'react-hot-toast'

// Templates based on business type
const facilityTemplates = {
  ice_rink: [
    { name: 'Main Ice Rink', capacity: 200, amenities: ['Zamboni', 'Scoreboard', 'Sound System'] },
    { name: 'Practice Rink', capacity: 100, amenities: ['Scoreboard'] },
    { name: 'Locker Room A', capacity: 25, amenities: ['Showers', 'Lockers'] },
    { name: 'Locker Room B', capacity: 25, amenities: ['Showers', 'Lockers'] }
  ],
  training_center: [
    { name: 'Weight Room', capacity: 50, amenities: ['Free Weights', 'Machines', 'Cardio Equipment'] },
    { name: 'Studio A', capacity: 30, amenities: ['Mirrors', 'Sound System', 'Mats'] },
    { name: 'Studio B', capacity: 20, amenities: ['Mirrors', 'Sound System'] },
    { name: 'Locker Rooms', capacity: 40, amenities: ['Showers', 'Lockers', 'Sauna'] }
  ],
  sports_complex: [
    { name: 'Field 1', capacity: 200, amenities: ['Lights', 'Scoreboard'] },
    { name: 'Field 2', capacity: 200, amenities: ['Lights', 'Scoreboard'] },
    { name: 'Indoor Court', capacity: 100, amenities: ['Basketball Hoops', 'Volleyball Net'] },
    { name: 'Meeting Room', capacity: 30, amenities: ['Projector', 'Whiteboard'] }
  ],
  dance_martial_arts: [
    { name: 'Main Studio', capacity: 40, amenities: ['Mirrors', 'Ballet Barre', 'Sound System'] },
    { name: 'Small Studio', capacity: 20, amenities: ['Mirrors', 'Mats'] },
    { name: 'Changing Rooms', capacity: 20, amenities: ['Lockers', 'Benches'] }
  ],
  other: []
}

export default function FacilitiesStep() {
  const { wizardData, updateStepData, getStepErrors } = useSetupWizard()
  const colors = useThemeColors()
  const { facilities, organizationProfile } = wizardData
  const errors = getStepErrors(3)
  const [newAmenity, setNewAmenity] = useState('')
  const [isAutoPopulating, setIsAutoPopulating] = useState(false)
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false)
  const [facilityLimit, setFacilityLimit] = useState({
    currentCount: 0,
    maxAllowed: null as number | null,
    canAddMore: true,
    isAtLimit: false,
    planCode: ''
  })

  // Get organization ID and subscription info on mount
  useEffect(() => {
    const getOrgInfo = async () => {
      if (!wizardData.organizationId) return
      
      // Check facility limits
      const limits = await checkFacilityLimit(wizardData.organizationId)
      setFacilityLimit(limits)
      
      // Auto-populate if needed
      if (facilities.length === 0 && !hasAutoPopulated && limits.planCode) {
        handleAutoPopulate(limits.planCode)
      }
    }
    getOrgInfo()
  }, [wizardData.organizationId])
  
  const handleAutoPopulate = async (planCode?: string) => {
    if (!wizardData.organizationId) return
    
    setIsAutoPopulating(true)
    try {
      const tierCode = planCode || facilityLimit.planCode
      if (!tierCode) {
        toast.error('Unable to determine subscription plan')
        return
      }
      
      const result = await autoPopulateFacilities(wizardData.organizationId, tierCode)
      
      if (result.success) {
        // Refresh facilities from database
        const { data: newFacilities } = await supabase
          .from('facilities')
          .select('*')
          .eq('organization_id', wizardData.organizationId)
          .order('display_order')
        
        if (newFacilities) {
          // Convert to wizard format
          const wizardFacilities = newFacilities.map(f => ({
            id: f.id,
            name: f.name,
            capacity: 0, // Not used in new system
            description: f.description || '',
            amenities: []
          }))
          updateStepData('facilities', wizardFacilities)
        }
        
        setHasAutoPopulated(true)
        toast.success(`Created ${result.facilitiesCreated} default facilities`)
      }
    } catch (error) {
      console.error('Error auto-populating facilities:', error)
      toast.error('Failed to create default facilities')
    } finally {
      setIsAutoPopulating(false)
    }
  }

  const addFacility = () => {
    // Check if at limit
    if (facilityLimit.isAtLimit) {
      toast.error(`You've reached the limit of ${facilityLimit.maxAllowed} facilities for your plan`)
      return
    }
    
    const newFacility = {
      id: `facility-${Date.now()}`,
      name: '',
      capacity: 0,
      description: '',
      amenities: []
    }
    updateStepData('facilities', [...facilities, newFacility])
  }

  const updateFacility = (id: string, field: string, value: string | number | string[]) => {
    const updated = facilities.map(facility =>
      facility.id === id ? { ...facility, [field]: value } : facility
    )
    updateStepData('facilities', updated)
  }

  const removeFacility = (id: string) => {
    const updated = facilities.filter(facility => facility.id !== id)
    updateStepData('facilities', updated)
  }

  const addAmenity = (facilityId: string) => {
    if (newAmenity.trim()) {
      const facility = facilities.find(f => f.id === facilityId)
      if (facility) {
        updateFacility(facilityId, 'amenities', [...(facility.amenities || []), newAmenity.trim()])
        setNewAmenity('')
      }
    }
  }

  const removeAmenity = (facilityId: string, amenity: string) => {
    const facility = facilities.find(f => f.id === facilityId)
    if (facility) {
      updateFacility(facilityId, 'amenities', facility.amenities?.filter(a => a !== amenity) || [])
    }
  }

  const loadTemplates = () => {
    const templates = facilityTemplates[organizationProfile.businessType] || []
    const newFacilities = templates.map((template, index) => ({
      id: `facility-${Date.now()}-${index}`,
      ...template
    }))
    updateStepData('facilities', newFacilities)
  }

  return (
    <div className="facilities-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Set up your facilities
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Add the spaces where you'll be hosting activities. You can always add more later.
      </p>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.error}20`,
          border: `1px solid ${colors.error}40`,
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.error,
            margin: 0
          }}>
            {errors[0]}
          </p>
        </div>
      )}

      {/* Auto-populate or Template Suggestion */}
      {facilities.length === 0 && !hasAutoPopulated && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}30`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.text,
            margin: 0
          }}>
            {facilityLimit.planCode 
              ? `🎯 Your ${facilityLimit.planCode} plan includes default facilities`
              : '💡 Want to start with common facilities for your business type?'
            }
          </p>
          <button
            onClick={() => facilityLimit.planCode ? handleAutoPopulate() : loadTemplates()}
            disabled={isAutoPopulating}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.813rem',
              fontWeight: '500',
              cursor: 'pointer',
              opacity: isAutoPopulating ? 0.7 : 1
            }}
          >
            {isAutoPopulating ? 'Creating...' : facilityLimit.planCode ? 'Create Default Facilities' : 'Use Template'}
          </button>
        </div>
      )}
      
      {/* Facility Limit Info */}
      {facilityLimit.maxAllowed && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: facilityLimit.isAtLimit ? `${colors.error}10` : `${colors.success}10`,
          border: `1px solid ${facilityLimit.isAtLimit ? `${colors.error}30` : `${colors.success}30`}`,
          borderRadius: '6px',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          color: facilityLimit.isAtLimit ? colors.error : colors.text
        }}>
          {facilityLimit.isAtLimit 
            ? `You've reached your plan limit of ${facilityLimit.maxAllowed} facilities`
            : `${facilities.length} of ${facilityLimit.maxAllowed} facilities used`
          }
        </div>
      )}

      {/* Facilities List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {facilities.map((facility, index) => (
          <div
            key={facility.id}
            style={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '1.5rem'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: colors.text,
                margin: 0
              }}>
                Facility {index + 1}
              </h3>
              <button
                onClick={() => removeFacility(facility.id)}
                style={{
                  padding: '0.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.error,
                  cursor: 'pointer'
                }}
              >
                <TrashIcon style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.813rem',
                  fontWeight: '500',
                  marginBottom: '0.25rem',
                  color: colors.textSecondary
                }}>
                  Facility Name <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  type="text"
                  value={facility.name}
                  onChange={(e) => updateFacility(facility.id, 'name', e.target.value)}
                  placeholder="e.g., Main Ice Rink"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.813rem',
                  fontWeight: '500',
                  marginBottom: '0.25rem',
                  color: colors.textSecondary
                }}>
                  Capacity <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  type="number"
                  value={facility.capacity || ''}
                  onChange={(e) => updateFacility(facility.id, 'capacity', parseInt(e.target.value) || 0)}
                  placeholder="50"
                  min="1"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.813rem',
                fontWeight: '500',
                marginBottom: '0.25rem',
                color: colors.textSecondary
              }}>
                Amenities (optional)
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                {facility.amenities?.map((amenity, i) => (
                  <span
                    key={i}
                    style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: `${colors.primary}20`,
                      border: `1px solid ${colors.primary}40`,
                      borderRadius: '16px',
                      fontSize: '0.813rem',
                      color: colors.text,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {amenity}
                    <button
                      onClick={() => removeAmenity(facility.id, amenity)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                <input
                  type="text"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addAmenity(facility.id)
                    }
                  }}
                  placeholder="Add amenity (e.g., Projector)"
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.813rem'
                  }}
                />
                <button
                  onClick={() => addAmenity(facility.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: colors.primary,
                    color: colors.background,
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Facility Button */}
      <button
        onClick={addFacility}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: 'transparent',
          border: `2px dashed ${colors.border}`,
          borderRadius: '8px',
          color: colors.primary,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primary
          e.currentTarget.style.backgroundColor = `${colors.primary}10`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <PlusIcon style={{ width: '20px', height: '20px' }} />
        Add Facility
      </button>
    </div>
  )
}